import { createReducer, isAnyOf } from '@reduxjs/toolkit'
import mergeWith from 'lodash/fp/mergeWith'
import { normalize } from 'normalizr'

import {
    createAuthPageOrganizationSuccess,
    setUserIsImpersonating,
    signOutSuccess,
} from 'actions/auth'
import {
    fetchOrganizationGroupMembersSuccess,
    fetchOrganizationGroupsSuccess,
    fetchOrganizationGroupSuccess,
    fetchOrganizationMembersSuccess,
    fetchOrganizationSuccess,
} from 'actions/orgs'
import { mergeArray } from 'helpers/utilities'
import {
    organizationGroupListSchema,
    organizationGroupSchema,
    organizationSchema,
    userListSchema,
} from 'schemas'
import { Organization } from 'types'
import type { OrgsState } from 'types/redux/orgs'

const defaultState: OrgsState = {
    organizations: {},
    groups: {},
    users: {},
}

function normalizeOrganizationPayload(
    organization: Organization
): Organization {
    const raw = organization.fiscal_year_start_month

    if (raw == null) {
        return { ...organization, fiscal_year_start_month: undefined }
    }

    const month = Number(raw)
    return {
        ...organization,
        fiscal_year_start_month: month >= 1 && month <= 12 ? month : undefined,
    }
}

export default createReducer(defaultState, (builder) => {
    builder
        .addCase(fetchOrganizationGroupSuccess, (state, action) => {
            const {
                entities: { groups, organizations, users },
            } = normalize(action.payload, organizationGroupSchema)

            return mergeWith(mergeArray, state, {
                groups,
                organizations,
                users,
            })
        })
        .addCase(fetchOrganizationGroupsSuccess, (state, action) => {
            const {
                entities: { groups, users, organizations },
            } = normalize(action.payload.results, organizationGroupListSchema)

            return mergeWith(mergeArray, state, {
                groups,
                organizations,
                users,
            })
        })
        .addCase(fetchOrganizationMembersSuccess, (state, action) => {
            const { members, organizationId } = action.payload
            if (members) {
                const {
                    entities: { users },
                    result,
                } = normalize(members, userListSchema)

                return mergeWith(mergeArray, state, {
                    users,
                    organizations: {
                        [organizationId]: {
                            members: result,
                        },
                    },
                })
            }
            return state
        })
        .addCase(fetchOrganizationGroupMembersSuccess, (state, action) => {
            const { members, organizationGroupId } = action.payload
            const {
                entities: { users },
                result,
            } = normalize(members, userListSchema)

            return mergeWith(mergeArray, state, {
                users,
                groups: {
                    [organizationGroupId]: {
                        members: result,
                    },
                },
            })
        })
        .addMatcher(
            isAnyOf(
                fetchOrganizationSuccess,
                createAuthPageOrganizationSuccess
            ),
            (state, action) => {
                const normalizedPayload = normalizeOrganizationPayload(
                    action.payload
                )
                const {
                    entities: { organizations, users },
                } = normalize(normalizedPayload, organizationSchema)

                const nextState = mergeWith(mergeArray, state, {
                    organizations,
                    users,
                })

                const orgId = String(normalizedPayload.id)
                const shouldClearFiscalYearStartMonth =
                    'fiscal_year_start_month' in action.payload &&
                    normalizedPayload.fiscal_year_start_month === undefined

                if (
                    shouldClearFiscalYearStartMonth &&
                    nextState.organizations[orgId]
                ) {
                    return {
                        ...nextState,
                        organizations: {
                            ...nextState.organizations,
                            [orgId]: {
                                ...nextState.organizations[orgId],
                                fiscal_year_start_month: undefined,
                            },
                        },
                    }
                }

                return nextState
            }
        )
        // Impersonation Mode / sign out: reset org state
        .addMatcher(
            isAnyOf(setUserIsImpersonating, signOutSuccess),
            () => defaultState
        )
})
